import type { TypeCode } from './types'

// ===== 数据结构定义 =====

export interface EnvironmentMatch {
  // 你可能感到自在的环境特征
  comfortable: {
    structureCertainty: string   // 结构确定性
    evaluationVisibility: string // 评价外显度
    autonomyLevel: string        // 自主决策度
  }

  // 你可能需要适应的环境特征
  challenging: string[]  // 3 条

  // 辅助信息
  supplementary: {
    collaborationLevel: string  // 协作依赖度
    resultCycle: string         // 成果周期
    workObject: string          // 工作对象
  }

  // 自检问题
  selfCheckQuestions: string[]  // 3 个问题
}

// ===== 16 种类型环境匹配数据 =====

export const ENVIRONMENT_MATCH_DATA: Record<TypeCode, EnvironmentMatch> = {
  // ========== 第一组：A + S 开头 ==========

  // ASID: 稳定清晰 + 内隐延迟 + 自主导向
  ASID: {
    comfortable: {
      structureCertainty: '你可能更自在于流程清晰、规则明确的环境。系统化的工作方式（S）加上对确定性的偏好（D），让你在有章可循、结果可预期的环境中高效运转。',
      evaluationVisibility: '你可能不太需要频繁的外部反馈。内部锚定（A）让你有自己的评判标准，内在驱动（I）让你更看重工作本身的深度和意义，而非外界的即时认可。',
      autonomyLevel: '你可能需要较大的自主空间，按照自己的节奏和标准推进工作。过度的指令和干预可能会消耗你的能量，你倾向于独立完成高质量的成果。',
    },
    challenging: [
      '规则模糊、方向频繁变动的混乱环境，可能让你难以建立稳定的工作节奏',
      '需要频繁汇报、接受即时评价的高曝光场合，可能让你感到内在标准被忽视',
      '执行细节被高度规定、没有判断空间的机械性工作，可能让你感到能力受限',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立思考后再交流，先形成自己的想法再与他人碰撞。协作时，你可能偏好有准备的讨论而非即兴脑暴。',
      resultCycle: '你能接受一定的工作周期，但需要阶段性的完成感来维持动力。明确的里程碑和可交付成果对你有正向作用。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域和专业背景。',
    },
    selfCheckQuestions: [
      '你目前的工作流程和目标是否足够清晰？当遇到模糊不确定的情况时，你通常如何应对？',
      '你是否有足够的空间按自己的判断和节奏工作，还是经常被打断或被要求按他人的方式做事？',
      '这份工作是否让你感到有意义和成长，还是只是在完成任务换取报酬？',
    ],
  },

  // ASIO: 中间 + 内隐延迟 + 自主导向
  ASIO: {
    comfortable: {
      structureCertainty: '你可能喜欢有方法论支撑、但问题本身开放的环境。系统化倾向（S）让你需要结构化的方法，探索取向（O）让你享受在未知领域中寻找答案。',
      evaluationVisibility: '你可能不太需要频繁的外部反馈。你有自己的评判标准（A），更看重工作本身的深度和意义（I），而非外界的即时认可。',
      autonomyLevel: '你可能需要较大的自主空间，按照自己的节奏和标准推进工作。过度的指令和干预可能会消耗你的能量。',
    },
    challenging: [
      '既没有方法指引、问题也不明确的失序状态，可能让你难以发挥系统化的优势',
      '需要频繁汇报、接受即时评价的高曝光场合，可能让你感到被打扰',
      '执行细节被高度规定、没有自主空间的工作，可能让你感到创造力被压制',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立思考后再交流，先形成自己的想法再与他人碰撞。',
      resultCycle: '你能接受长线积累，享受探索过程本身带来的满足感。但你可能也需要阶段性的新发现来保持兴趣。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你是否有足够的空间按自己的判断和节奏工作，还是经常被打断或被要求按他人的方式做事？',
      '这份工作是否让你感到有意义和成长，还是只是在完成任务换取报酬？',
      '你是否有机会用系统化的方法探索新领域，还是只能在既定框架内重复执行？',
    ],
  },

  // ASED: 稳定清晰 + 中间偏内隐 + 中间偏自主
  ASED: {
    comfortable: {
      structureCertainty: '你可能更自在于流程清晰、规则明确的环境。系统化的工作方式（S）加上对确定性的偏好（D），让你在有章可循、结果可预期的环境中高效运转。',
      evaluationVisibility: '你有自己的判断标准（A），但也关注外在成果和认可（E）。你可能喜欢有阶段性反馈、但不过度干预的环境，既能保持独立判断又能看到成果被认可。',
      autonomyLevel: '你可能喜欢目标明确但过程自主的环境。你能接受外部设定的目标（E），但希望在执行层面有自己的空间（A）。',
    },
    challenging: [
      '规则模糊、方向频繁变动的混乱环境，可能让你难以建立稳定的工作节奏',
      '完全没有反馈或反馈过于密集的极端，可能让你难以把握成果的价值',
      '目标和过程都被严格规定的机械执行，可能让你感到判断力被浪费',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立思考后再交流，先形成自己的想法再与他人碰撞。协作时，你可能偏好有准备的讨论。',
      resultCycle: '你可能偏好能快速看到成果的项目。明确的短期目标和阶段性反馈，往往能保持你的动力。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你目前的工作流程和目标是否足够清晰？当遇到模糊不确定的情况时，你通常如何应对？',
      '你在工作中是否既能保持自己的判断，又能获得足够的外部认可？两者是否平衡？',
      '当你想尝试新方法或新方向时，环境是支持还是阻碍？你是否有足够的执行自主权？',
    ],
  },

  // ASEO: 中间 + 中间偏内隐 + 中间偏自主
  ASEO: {
    comfortable: {
      structureCertainty: '你可能喜欢有方法论支撑、但问题本身开放的环境。系统化倾向（S）让你需要结构化的方法，探索取向（O）让你享受开放性的挑战。',
      evaluationVisibility: '你有自己的判断标准（A），但也关注外在成果和认可（E）。你可能喜欢有阶段性反馈、但不过度干预的环境。',
      autonomyLevel: '你可能喜欢目标明确但过程自主的环境。你能接受外部设定的目标，但希望在执行层面有自己的空间。',
    },
    challenging: [
      '既没有方法指引、问题也不明确的失序状态，可能让你难以发挥',
      '完全没有反馈或反馈过于密集的极端，可能让你难以把握成果的价值',
      '目标和过程都被严格规定的机械执行，可能让你感到受限',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立思考后再交流，先形成自己的想法再与他人碰撞。',
      resultCycle: '你需要阶段性的反馈和认可（E），但过程本身可以保持开放（O）。中等周期的项目，既有产出又有探索空间，可能比较适合你。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你是否有机会用系统化的方法探索新领域，还是只能在既定框架内执行？',
      '你在工作中是否既能保持自己的判断，又能获得足够的外部认可？',
      '你的付出是否得到了应有的认可和回报？反馈的频率和质量如何？',
    ],
  },

  // ========== 第二组：A + C 开头 ==========

  // ACID: 中间 + 内隐延迟 + 自主导向
  ACID: {
    comfortable: {
      structureCertainty: '你可能喜欢有一定灵活度、但目标明确的环境。情境化倾向（C）让你能随机应变，闭合取向（D）让你追求阶段性的确定感和完成感。',
      evaluationVisibility: '你可能不太需要频繁的外部反馈。你有自己的评判标准（A），更看重工作本身的意义（I），而非外界的即时认可。',
      autonomyLevel: '你可能需要较大的自主空间，按照自己的节奏和标准推进工作。你倾向于用自己的方式解决问题。',
    },
    challenging: [
      '过于僵化或目标频繁变动的环境，可能让你难以在灵活和确定之间找到平衡',
      '需要频繁汇报、接受即时评价的高曝光场合，可能让你感到内在节奏被打乱',
      '执行细节被高度规定、没有应变空间的工作，可能让你感到灵活性被压制',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立思考后再交流，在需要时能灵活调整沟通方式。',
      resultCycle: '你能接受一定的工作周期，但需要阶段性的完成感来维持动力。你倾向于在完成一个阶段后再开始下一个。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你是否有空间根据情况灵活调整做事方式，还是必须严格遵循既定流程？',
      '你是否有足够的空间按自己的判断行事，还是经常需要等待他人的指示？',
      '这份工作是否让你感到有意义，还是只是在完成任务？',
    ],
  },

  // ACIO: 流动探索 + 内隐延迟 + 自主导向
  ACIO: {
    comfortable: {
      structureCertainty: '你可能更自在于变化多、边界模糊的环境。情境化倾向（C）让你适应模糊和变化，探索取向（O）让你享受灵活应变和开放可能性。',
      evaluationVisibility: '你可能不太需要频繁的外部反馈。你有自己的评判标准（A），更看重探索过程本身的意义（I），而非外界的即时认可。',
      autonomyLevel: '你可能需要较大的自主空间，按照自己的节奏和标准推进工作。你倾向于在探索中发现自己的方向。',
    },
    challenging: [
      '过于固定、缺乏变化的重复性工作，可能让你感到沉闷和受限',
      '需要频繁汇报、接受即时评价的高曝光场合，可能打断你的探索节奏',
      '执行细节被高度规定、没有自主空间的工作，可能让你感到创造力被压制',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立探索后再交流，在有了自己的发现后与他人分享和碰撞。',
      resultCycle: '你能接受长线积累，享受探索过程本身带来的满足感。对你来说，过程中的发现可能比最终结果更让你兴奋。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你目前的工作中，有多少空间让你灵活应变和探索新可能？这个比例让你满意吗？',
      '你是否有足够的空间按自己的判断和节奏工作？',
      '这份工作是否让你感到有意义和成长，还是只是在重复既有的模式？',
    ],
  },

  // ACED: 中间 + 中间偏内隐 + 中间偏自主
  ACED: {
    comfortable: {
      structureCertainty: '你可能喜欢有一定灵活度、但目标明确的环境。情境化倾向（C）让你能随机应变，闭合取向（D）让你追求阶段性的确定感。',
      evaluationVisibility: '你有自己的判断标准（A），但也关注外在成果和认可（E）。你可能喜欢有阶段性反馈、但不过度干预的环境。',
      autonomyLevel: '你可能喜欢目标明确但过程自主的环境。你能接受外部设定的目标，但希望在执行层面有灵活应变的空间。',
    },
    challenging: [
      '过于僵化或目标频繁变动的环境，可能让你难以把握节奏',
      '完全没有反馈或反馈过于密集的极端，可能让你难以确认成果价值',
      '目标和过程都被严格规定的机械执行，可能让你感到灵活性被限制',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立思考后再交流，但在需要时能灵活调整沟通方式。',
      resultCycle: '你可能偏好能快速看到成果的项目。明确的短期目标和阶段性反馈，往往能保持你的动力。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你是否有空间根据情况灵活调整做事方式，同时又能获得明确的目标指引？',
      '你在工作中是否既能保持自己的判断，又能获得足够的外部认可？',
      '你的付出是否得到了应有的认可和回报？',
    ],
  },

  // ACEO: 流动探索 + 中间偏内隐 + 中间偏自主
  ACEO: {
    comfortable: {
      structureCertainty: '你可能更自在于变化多、边界模糊的环境。情境化倾向（C）让你适应模糊和变化，探索取向（O）让你享受开放可能性。',
      evaluationVisibility: '你有自己的判断标准（A），但也关注外在成果和认可（E）。你可能喜欢有阶段性反馈、但不限制探索方向的环境。',
      autonomyLevel: '你可能喜欢目标明确但过程自主的环境。你能接受外部设定的大方向，但希望在执行层面有灵活探索的空间。',
    },
    challenging: [
      '过于固定、缺乏变化的重复性工作，可能让你感到沉闷',
      '完全没有反馈或反馈过于密集的极端，可能让你难以平衡',
      '目标和过程都被严格规定的机械执行，可能让你感到受限',
    ],
    supplementary: {
      collaborationLevel: '你可能更偏好独立探索后再交流，在有了自己的发现后与他人分享。',
      resultCycle: '你需要阶段性的反馈和认可（E），但过程本身可以保持开放（O）。',
      workObject: '对于工作对象（数据、产品、人等），你没有明显的天然偏好，这取决于你的具体兴趣领域。',
    },
    selfCheckQuestions: [
      '你目前的工作中，有多少空间让你灵活应变和探索新可能？',
      '你在工作中是否既能保持自己的判断，又能获得足够的外部认可？',
      '你的付出是否得到了应有的认可和回报？反馈的频率如何？',
    ],
  },

  // ========== 第三组：R + S 开头 ==========

  // RSID: 稳定清晰 + 中间偏外显 + 中间偏执行
  RSID: {
    comfortable: {
      structureCertainty: '你可能更自在于流程清晰、规则明确的环境。系统化的工作方式（S）加上对确定性的偏好（D），让你在有章可循的环境中高效运转。',
      evaluationVisibility: '你可能需要一定的外部参照来校准方向（R），但工作本身的意义感（I）对你同样重要。适度的反馈能帮你确认自己走在正确的路上。',
      autonomyLevel: '你可能能够接受一定的外部指引（R），但前提是工作本身要有意义（I）。你不介意配合大方向，但需要感受到自己在做有价值的事。',
    },
    challenging: [
      '规则模糊、方向频繁变动的混乱环境，可能让你难以建立稳定的工作节奏',
      '长期独立工作、缺乏外部输入的孤立环境，可能让你难以校准方向',
      '无意义的重复执行，可能让你逐渐失去投入的动力',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中校准想法，团队协作对你有正向作用。但你也需要一定的独处时间来思考意义。',
      resultCycle: '你能接受一定的工作周期，但需要阶段性的完成感和反馈来维持动力。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照。但这不是绝对的，具体取决于你的兴趣领域。',
    },
    selfCheckQuestions: [
      '你目前的工作流程和目标是否足够清晰？当遇到模糊情况时，你是否能获得足够的指引？',
      '你是否能获得足够的反馈来确认自己的方向？这些反馈的质量如何？',
      '这份工作是否让你感到有意义，还是只是在完成任务？',
    ],
  },

  // RSIO: 中间 + 中间偏外显 + 中间偏执行
  RSIO: {
    comfortable: {
      structureCertainty: '你可能喜欢有方法论支撑、但问题本身开放的环境。系统化倾向（S）让你需要结构化的方法，探索取向（O）让你享受开放性的挑战。',
      evaluationVisibility: '你可能需要一定的外部参照来校准方向（R），但工作本身的意义感（I）对你同样重要。',
      autonomyLevel: '你可能能够接受一定的外部指引（R），但前提是工作本身要有意义（I）。',
    },
    challenging: [
      '既没有方法指引、问题也不明确的失序状态，可能让你难以发挥',
      '长期独立工作、缺乏外部输入的孤立环境，可能让你难以校准方向',
      '无意义的重复执行，可能让你逐渐失去投入的动力',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中形成想法，通过交流来激发和校准思路。',
      resultCycle: '你能接受长线积累，但需要阶段性的反馈来确认方向。享受探索过程本身带来的满足感。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照。但具体取决于你的兴趣领域。',
    },
    selfCheckQuestions: [
      '你是否有机会用系统化的方法探索新领域？',
      '你是否能获得足够的反馈来确认自己的方向？这些反馈是否有质量？',
      '这份工作是否让你感到有意义和成长？',
    ],
  },

  // RSED: 稳定清晰 + 外显即时 + 可适应执行
  RSED: {
    comfortable: {
      structureCertainty: '你可能更自在于流程清晰、规则明确的环境。系统化的工作方式（S）加上对确定性的偏好（D），让你在有章可循的环境中高效运转。',
      evaluationVisibility: '你可能需要清晰、及时的外部反馈来保持动力。外部锚定（R）让你参考他人反馈，外在驱动（E）让你看重成果认可。明确的认可机制和可见的进展能让你更有方向感。',
      autonomyLevel: '你可能能够在清晰的外部框架中高效运转。明确的指引和目标（R+E）能帮你集中精力，你不太需要从零开始定义方向。',
    },
    challenging: [
      '规则模糊、方向频繁变动的混乱环境，可能让你难以建立节奏',
      '反馈模糊、认可滞后的不透明环境，可能让你逐渐失去动力',
      '需要自己从零定义方向和规则的模糊地带，可能让你感到缺乏抓手',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中形成想法，频繁的沟通交流通常不会让你感到消耗。',
      resultCycle: '你可能偏好短线闭环，能够快速看到结果和反馈。明确的短期目标和及时的认可，往往能保持你的动力。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照和即时反馈。',
    },
    selfCheckQuestions: [
      '你目前的工作流程和目标是否足够清晰？',
      '你获得的反馈和认可，频率和及时性如何？是否足够支撑你的持续投入？',
      '你目前工作中的协作体验如何？是否有足够的沟通、支持和明确的指引？',
    ],
  },

  // RSEO: 中间 + 外显即时 + 可适应执行
  RSEO: {
    comfortable: {
      structureCertainty: '你可能喜欢有方法论支撑、但问题本身开放的环境。系统化倾向（S）让你需要结构化的方法，探索取向（O）让你享受开放性的挑战。',
      evaluationVisibility: '你可能需要清晰、及时的外部反馈来保持动力。明确的认可机制和可见的进展能让你更有方向感。',
      autonomyLevel: '你可能能够在清晰的外部框架中高效运转。明确的指引和目标能帮你集中精力。',
    },
    challenging: [
      '既没有方法指引、问题也不明确的失序状态，可能让你难以发挥',
      '反馈模糊、认可滞后的不透明环境，可能让你逐渐失去动力',
      '需要自己从零定义方向和规则的模糊地带，可能让你感到迷茫',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中形成想法，频繁的沟通交流通常不会让你感到消耗。',
      resultCycle: '你需要阶段性的反馈和认可（E），但过程本身可以保持开放（O）。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照和即时反馈。',
    },
    selfCheckQuestions: [
      '你是否有机会用系统化的方法探索新领域？',
      '你获得的反馈和认可，频率和及时性如何？',
      '你目前工作中的协作体验如何？是否有足够的沟通和支持？',
    ],
  },

  // ========== 第四组：R + C 开头 ==========

  // RCID: 中间 + 中间偏外显 + 中间偏执行
  RCID: {
    comfortable: {
      structureCertainty: '你可能喜欢有一定灵活度、但目标明确的环境。情境化倾向（C）让你能随机应变，闭合取向（D）让你追求阶段性的确定感和完成感。',
      evaluationVisibility: '你可能需要一定的外部参照来校准方向（R），但工作本身的意义感（I）对你同样重要。适度的反馈能帮你确认自己走在正确的路上。',
      autonomyLevel: '你可能能够接受一定的外部指引（R），但前提是工作本身要有意义（I）。你不介意配合大方向，但需要感受到自己在做有价值的事。',
    },
    challenging: [
      '过于僵化或目标频繁变动的环境，可能让你难以把握节奏',
      '长期独立工作、缺乏外部输入的孤立环境，可能让你难以校准方向',
      '无意义的重复执行，可能让你逐渐失去投入的动力',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中校准想法，团队协作对你有正向作用。',
      resultCycle: '你能接受一定的工作周期，但需要阶段性的完成感和反馈来维持动力。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照。但具体取决于你的兴趣领域。',
    },
    selfCheckQuestions: [
      '你是否有空间根据情况灵活调整做事方式，同时又能获得明确的目标指引？',
      '你是否能获得足够的反馈来确认自己的方向？',
      '这份工作是否让你感到有意义，还是只是在完成任务？',
    ],
  },

  // RCIO: 流动探索 + 中间偏外显 + 中间偏执行
  RCIO: {
    comfortable: {
      structureCertainty: '你可能更自在于变化多、边界模糊的环境。情境化倾向（C）让你适应模糊和变化，探索取向（O）让你享受灵活应变和开放可能性。',
      evaluationVisibility: '你可能需要一定的外部参照来校准方向（R），但工作本身的意义感（I）对你同样重要。',
      autonomyLevel: '你可能能够接受一定的外部指引（R），但前提是工作本身要有意义（I）。',
    },
    challenging: [
      '过于固定、缺乏变化的重复性工作，可能让你感到沉闷',
      '长期独立工作、缺乏外部输入的孤立环境，可能让你难以校准方向',
      '无意义的重复执行，可能让你逐渐失去动力',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中形成想法，通过交流来激发和校准思路。',
      resultCycle: '你能接受长线积累，但需要阶段性的反馈来确认方向。享受探索过程本身带来的满足感。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照。但具体取决于你的兴趣领域。',
    },
    selfCheckQuestions: [
      '你目前的工作中，有多少空间让你灵活应变和探索新可能？',
      '你是否能获得足够的反馈来确认自己的方向？',
      '这份工作是否让你感到有意义和成长？',
    ],
  },

  // RCED: 中间 + 外显即时 + 可适应执行
  RCED: {
    comfortable: {
      structureCertainty: '你可能喜欢有一定灵活度、但目标明确的环境。情境化倾向（C）让你能随机应变，闭合取向（D）让你追求阶段性的确定感。',
      evaluationVisibility: '你可能需要清晰、及时的外部反馈来保持动力。明确的认可机制和可见的进展能让你更有方向感。',
      autonomyLevel: '你可能能够在清晰的外部框架中高效运转，同时保持执行层面的灵活性。',
    },
    challenging: [
      '过于僵化或目标频繁变动的环境，可能让你难以把握节奏',
      '反馈模糊、认可滞后的不透明环境，可能让你逐渐失去动力',
      '需要自己从零定义方向和规则的模糊地带，可能让你感到缺乏抓手',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中形成想法，频繁的沟通交流通常不会让你感到消耗。',
      resultCycle: '你可能偏好短线闭环，能够快速看到结果和反馈。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照和即时反馈。',
    },
    selfCheckQuestions: [
      '你是否有空间根据情况灵活调整做事方式，同时又能获得明确的目标指引？',
      '你获得的反馈和认可，频率和及时性如何？',
      '你目前工作中的协作体验如何？是否有足够的沟通和支持？',
    ],
  },

  // RCEO: 流动探索 + 外显即时 + 可适应执行
  RCEO: {
    comfortable: {
      structureCertainty: '你可能更自在于变化多、边界模糊的环境。情境化倾向（C）让你适应模糊和变化，探索取向（O）让你享受开放可能性。',
      evaluationVisibility: '你可能需要清晰、及时的外部反馈来保持动力。明确的认可机制和可见的进展能让你更有方向感。',
      autonomyLevel: '你可能能够在清晰的外部框架中高效运转，同时保持探索和应变的灵活性。',
    },
    challenging: [
      '过于固定、缺乏变化的重复性工作，可能让你感到沉闷',
      '反馈模糊、认可滞后的不透明环境，可能让你逐渐失去动力',
      '需要自己从零定义方向和规则的模糊地带，可能让你感到迷茫',
    ],
    supplementary: {
      collaborationLevel: '你可能更适应在互动中形成想法，频繁的沟通交流通常不会让你感到消耗。',
      resultCycle: '你需要阶段性的反馈和认可（E），但过程本身可以保持开放（O）。',
      workObject: '与人互动较多的工作可能为你提供更多外部参照和即时反馈。',
    },
    selfCheckQuestions: [
      '你目前的工作中，有多少空间让你灵活应变和探索新可能？',
      '你获得的反馈和认可，频率和及时性如何？是否足够支撑你的持续投入？',
      '你目前工作中的协作体验如何？是否有足够的沟通和支持？',
    ],
  },
}

// ===== 获取环境匹配数据的便捷函数 =====

export function getEnvironmentMatch(typeCode: TypeCode): EnvironmentMatch {
  return ENVIRONMENT_MATCH_DATA[typeCode]
}
